'use client';

import {
  useState,
  createContext,
  useMemo,
  useCallback,
  type PropsWithChildren
} from 'react';
import { observer } from 'mobx-react-lite';

/**
 * Interface for the grid layout state.
 */
interface IGridLayoutState {
  /**
   * Currently focused item.
   */
  idx: number;
  /**
   * Whether the grid is focused on an item or not.
   */
  focused: boolean;
}

/**
 * Tracks the state of the grid layout, and provides functions to set the state.
 */
interface IGridLayoutContext {
  /**
   * The state of the grid layout.
   */
  gridState: IGridLayoutState;
  /**
   * Sets the selected item in the grid.
   * @param idx
   */
  setSelectedItem: (idx: number) => void;
  /**
   * Toggles the focused state of the grid.
   * @param focused
   */
  setFocused: (focused: boolean) => void;
}

/**
 * Context for the grid layout.
 */
export const FocusableGridContext = createContext<IGridLayoutContext>({
  gridState: { focused: false, idx: 0 },
  setSelectedItem: () => {},
  setFocused: () => {}
});

/**
 * Tracks whether the grid is focused or not,
 * and which item is selected.
 */
export const FocusableGridProvider = observer(function FocusableGridProvider({
  children
}: PropsWithChildren) {
  const [gridState, setGridState] = useState<IGridLayoutState>({
    focused: false,
    idx: 0
  });

  const setSelectedItem = useCallback((idx: number) => {
    setGridState({ focused: true, idx });
  }, []);

  const setFocused = useCallback((focused: boolean) => {
    setGridState((prevState) => ({ ...prevState, focused }));
  }, []);

  const context = useMemo(() => {
    return { gridState, setSelectedItem, setFocused };
  }, [gridState, setFocused, setSelectedItem]);

  return (
    <FocusableGridContext value={context}>{children}</FocusableGridContext>
  );
});
